import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "../components/ui/table";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

export default function VatAccount() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/report/vat`);
        setReport(data);
      } catch (err) {
        console.error(err);
        toast.error("KDV Raporu çekilemedi.");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const formatCurrency = (val) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val || 0);

  const formatPeriod = (periodStr) => {
    try {
      // periodStr is "YYYY-MM"
      const date = parseISO(`${periodStr}-01`);
      return format(date, "MMMM yyyy", { locale: tr }); // e.g. "Ocak 2024"
    } catch {
      return periodStr;
    }
  };

  const totals = useMemo(() => {
    return report.reduce(
      (acc, row) => {
        acc.sales_vat += row.sales_vat || 0;
        acc.purchases_vat += row.purchases_vat || 0;
        acc.net_vat += row.net_vat || 0;
        acc.vat_payment += row.vat_payment || 0;
        return acc;
      },
      { sales_vat: 0, purchases_vat: 0, net_vat: 0, vat_payment: 0 }
    );
  }, [report]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      <div className="flex justify-between items-center shrink-0">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Aylık KDV Hesabı Raporu</h1>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden flex-1 flex flex-col">
        <Table className="whitespace-nowrap w-full">
          <TableHeader className="bg-white sticky top-0 z-20 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <TableRow className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border hover:bg-white">
              <TableHead className="h-12 px-6 align-middle font-semibold text-neutral-800">Dönem (Ay-Yıl)</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-red-600">Hesaplanan KDV (Satış Faturası)</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-green-600">İndirilecek KDV (Alış Faturası)</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-blue-600">Bu Ayki Net KDV</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10">Yükleniyor...</TableCell></TableRow>
            ) : report.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-neutral-500">Hiç fatura kaydı bulunamadı.</TableCell></TableRow>
            ) : (
              report.map((row) => (
                <TableRow key={row.period} className="text-sm hover:bg-neutral-50 transition-colors border-b border-border last:border-0">
                  <TableCell className="px-6 py-4 align-middle font-medium capitalize">{formatPeriod(row.period)}</TableCell>
                  <TableCell className="px-6 py-4 align-middle text-right text-red-600 font-semibold">{formatCurrency(row.sales_vat)}</TableCell>
                  <TableCell className="px-6 py-4 align-middle text-right text-green-600 font-semibold">{formatCurrency(row.purchases_vat)}</TableCell>
                  <TableCell className={`px-6 py-4 align-middle text-right font-bold ${row.net_vat > 0 ? "text-green-600" : row.net_vat < 0 ? "text-red-600" : "text-neutral-500"}`}>
                    {row.net_vat > 0 ? '+' : ''}{formatCurrency(row.net_vat)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold bg-neutral-100 hover:bg-neutral-100 shadow-[0_-1px_2px_rgba(0,0,0,0.05)] sticky bottom-0 z-20">
              <TableCell className="px-6 py-4 align-middle text-right">GENEL TOPLAM</TableCell>
              <TableCell className="px-6 py-4 align-middle text-right text-red-600">{formatCurrency(totals.sales_vat)}</TableCell>
              <TableCell className="px-6 py-4 align-middle text-right text-green-600">{formatCurrency(totals.purchases_vat)}</TableCell>
              <TableCell className={`px-6 py-4 align-middle text-right ${totals.net_vat > 0 ? "text-green-600" : totals.net_vat < 0 ? "text-red-600" : "text-neutral-500"}`}>
                {totals.net_vat > 0 ? '+' : ''}{formatCurrency(totals.net_vat)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      <div className="flex justify-between items-center shrink-0 pt-8 border-t border-border">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Aylık KDV Ödemeleri ve Devir Takibi</h2>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden flex-1 flex flex-col">
        <Table className="whitespace-nowrap w-full">
          <TableHeader className="bg-white sticky top-0 z-20 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <TableRow className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border hover:bg-white">
              <TableHead className="h-12 px-6 align-middle font-semibold text-neutral-800">Dönem (Ay-Yıl)</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-orange-600 bg-orange-50/20">Geçmişten Devreden KDV Alacağı</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-blue-600">O Ayın Net KDV&apos;si</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right text-red-600 bg-red-50/30">Devlete Ödenecek KDV (Eksiye Düşüldü)</TableHead>
              <TableHead className="h-12 px-6 align-middle font-semibold text-right bg-blue-50/50">Sonraki Aya Devreden KDV</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10">Yükleniyor...</TableCell></TableRow>
            ) : report.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-neutral-500">Kayıt yok.</TableCell></TableRow>
            ) : (
              report.map((row) => (
                <TableRow key={`devir-${row.period}`} className="text-sm hover:bg-neutral-50 transition-colors border-b border-border last:border-0">
                  <TableCell className="px-6 py-4 align-middle font-medium capitalize">{formatPeriod(row.period)}</TableCell>
                  <TableCell className="px-6 py-4 align-middle text-right font-medium text-orange-600 bg-orange-50/10">
                    {row.prev_running_vat > 0 ? '+' : ''}{formatCurrency(row.prev_running_vat || 0)}
                  </TableCell>
                  <TableCell className={`px-6 py-4 align-middle text-right font-medium ${row.current_month_net > 0 ? "text-blue-600" : row.current_month_net < 0 ? "text-red-600" : "text-neutral-500"}`}>
                    {row.current_month_net > 0 ? '+' : ''}{formatCurrency(row.current_month_net)}
                  </TableCell>
                  <TableCell className="px-6 py-4 align-middle text-right font-bold text-red-600 bg-red-50/10">
                    {row.vat_payment > 0 ? formatCurrency(row.vat_payment) : "-"}
                  </TableCell>
                  <TableCell className={`px-6 py-4 align-middle text-right font-bold bg-blue-50/20 ${row.running_vat > 0 ? "text-blue-600" : "text-neutral-500"}`}>
                    {row.running_vat > 0 ? `+${formatCurrency(row.running_vat)}` : "0,00 ₺"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold bg-neutral-100 hover:bg-neutral-100 shadow-[0_-1px_2px_rgba(0,0,0,0.05)] sticky bottom-0 z-20">
              <TableCell colSpan={3} className="px-6 py-4 align-middle text-right">BUGÜNE KADAR DEVLETE ÖDENEN KDV</TableCell>
              <TableCell className="px-6 py-4 align-middle text-right text-red-600 bg-red-50/20">
                {totals.vat_payment > 0 ? formatCurrency(totals.vat_payment) : "-"}
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      
      <div className="bg-neutral-50 border rounded-lg p-4 text-sm text-neutral-600">
        <h4 className="font-semibold text-neutral-800 mb-2">Hesaplama Mantığı</h4>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li><strong>Hesaplanan KDV (Satış Faturası):</strong> O ay içinde müşteriye kestiğiniz SATIŞ faturalarınızın KDV toplamıdır. (Devlete olan KDV borcunuzu artırır).</li>
          <li><strong>İndirilecek KDV (Alış Faturası):</strong> O ay size kesilen ALIŞ (Gider/Harcama) faturalarının KDV toplamıdır. (Devletten olan KDV alacağınızı artırır).</li>
          <li><strong>Bu Ayki Net KDV:</strong> İndirilecek KDV - Hesaplanan KDV formülüyle bulunur.</li>
          <li><strong>Otomatik İşleme (Arka Plan):</strong> Eğer o aya kadar olan toplam bakiye eksiye düşerse &quot;Devlete Ödenecek KDV&quot; otomatik hesaplanıp İşlemler tablonuza o ayın 28&apos;i tarihiyle bir Gider işlemi olarak eklenir.</li>
        </ul>
      </div>
    </div>
  );
}
